import * as api from '@gitbook/api-client';
import { Icon, type SVGIconComponent } from '@gitbook/spine-icons';
import {
    Button,
    Heading,
    Menu,
    MenuItem,
    MenuRoot,
    MenuSection,
    MenuTrigger,
    Popover,
    toast,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import type { GitBookAgentClientState } from '../Agent';

/**
 * The scopes in the order they are offered: the default first, then progressively narrower.
 */
const EDIT_SCOPES: Array<{
    scope: api.AgentEditScope;
    label: string;
    description: string;
    icon: SVGIconComponent;
}> = [
    {
        scope: api.AgentEditScope.All,
        label: 'Auto',
        description: 'Edit wherever the task needs',
        icon: Icon.Space,
    },
    {
        scope: api.AgentEditScope.CurrentPage,
        label: 'Current page',
        description: 'Prefers this page, may edit related pages',
        icon: Icon.Page,
    },
    {
        scope: api.AgentEditScope.ModifiedPages,
        label: 'Changed pages',
        description: 'Prefers changed pages, may edit related pages',
        icon: Icon.DiffModified,
    },
    {
        scope: api.AgentEditScope.ReadOnly,
        label: 'Suggest',
        description: 'Review and suggest, no edits',
        icon: Icon.Eye,
    },
];

/**
 * Extractor for {@link useAgentState}, reading the conversation's edit scope.
 */
export const getAgentEditScope = (state: GitBookAgentClientState) => state.editScope;

/**
 * Picker for how far the conversation should reach when changing content, rendered in the
 * composer's toolbar next to where messages are written.
 */
export function AgentEditScopePicker(props: {
    /** The conversation's scope, null while it has not settled on one. */
    editScope: api.AgentEditScope | null;
    /**
     * Scope to display while the user has not chosen one. Should match the agent's own default,
     * {@link api.AgentEditScope.All}.
     * @default api.AgentEditScope.All
     */
    fallbackScope?: api.AgentEditScope;
    /**
     * Scopes that cannot be picked where the user is standing, with why — e.g. the page-scoped
     * ones outside a space. Shown disabled with the reason, rather than hidden: the picker is
     * also how the scopes are discovered. A scope the conversation is already in stays displayed
     * even when it is in this map; it just cannot be re-picked.
     */
    unavailableScopes?: Partial<Record<api.AgentEditScope, string>>;
    /** Apply the user's choice — the caller's agent RPC. Failures surface as a toast here. */
    onSelect: (scope: api.AgentEditScope) => Promise<void> | void;
}) {
    const {
        editScope,
        fallbackScope = api.AgentEditScope.All,
        unavailableScopes,
        onSelect,
    } = props;
    const currentScope = editScope ?? fallbackScope;
    const current = EDIT_SCOPES.find((def) => def.scope === currentScope) ?? EDIT_SCOPES[0];

    const onPick = useEventCallback((scope: api.AgentEditScope) => {
        if (scope === currentScope) {
            return;
        }
        void toast.catch(() => Promise.resolve(onSelect(scope)), 'Could not change the edit scope');
    });

    return (
        <MenuRoot>
            <MenuTrigger>
                <Button
                    kind="blank"
                    size="small"
                    leadingIcon={current.icon}
                    trailingIcon={Icon.ChevronDown}
                    label={current.label}
                    aria-label={`Edit scope: ${current.label}`}
                />
            </MenuTrigger>
            <Popover>
                <Menu>
                    <MenuSection>
                        <Heading>Edit scope</Heading>
                        {EDIT_SCOPES.map((def) => {
                            const unavailable = unavailableScopes?.[def.scope];
                            return (
                                <MenuItem
                                    key={def.scope}
                                    icon={def.icon}
                                    checked={def.scope === currentScope}
                                    disabled={Boolean(unavailable)}
                                    subtitle={unavailable ?? def.description}
                                    onAction={() => onPick(def.scope)}
                                >
                                    {def.label}
                                </MenuItem>
                            );
                        })}
                    </MenuSection>
                </Menu>
                <div className="border-t border-neutral-subtle">
                    <p className="max-w-72 px-3.5 py-2 text-ui-small text-muted">
                        Guides where the agent works. It may still edit related pages to keep links
                        and structure correct.
                    </p>
                </div>
            </Popover>
        </MenuRoot>
    );
}
